import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { offerApi, OfferStage } from '@/api/offerApi';
import { useUIStore } from '@/store/uiStore';
import { CreateOfferRequest } from '@/types/offer/offer.types';
import { getApiErrorMessage } from '@/utils';


// Người lao động ứng tuyển công việc
export function useApplyJob() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateOfferRequest) => offerApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-applied-jobs'] });
      addToast('success', 'Ứng tuyển thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Ứng tuyển thất bại, thử lại!')),
  });
}

// Người quản lý đề nghị người lao động công việc
export function useOfferJob() {
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateOfferRequest) => offerApi.create(data),
    onSuccess: () => addToast('success', 'Đã gửi đề nghị công việc thành công!'),
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi đề nghị thất bại, thử lại!')),
  });
}

// Người mua gửi đề nghị sang nhượng
export function useOfferTransfer() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateOfferRequest) => offerApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-transfer-offers'] });
      addToast('success', 'Đã gửi yêu cầu sang nhượng thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi yêu cầu thất bại, thử lại!')),
  });
}

// Bên cho thuê gửi đề nghị đến bên nhận thuê (nhu cầu)
export function useOfferTransferRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateOfferRequest) => offerApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-transfer-request-offers'] });
      addToast('success', 'Đã gửi đề nghị thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi đề nghị thất bại, thử lại!')),
  });
}


// Bên nhượng quyền gửi đề nghị đến bên nhận nhượng quyền (nhu cầu)
export function useOfferFranchise() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateOfferRequest) => offerApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-franchise-offers'] });
      addToast('success', 'Đã gửi yêu cầu nhượng quyền thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi yêu cầu thất bại, thử lại!')),
  });
}

// Người thuê gửi yêu cầu thuê mặt bằng
export function useOfferRental() {
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateOfferRequest) => offerApi.create(data),
    onSuccess: () => addToast('success', 'Đã gửi yêu cầu thuê thành công!'),
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi yêu cầu thất bại, thử lại!')),
  });
}


// Người quản lý nhận được đề nghị công việc
export function useRecruiterOffers(receiverId: string, params?: any) {
  return useQuery({
    queryKey: ['recruiter-offers', receiverId, params],
    queryFn: () => offerApi.getList({ entityType: 'JobOffer', receiverId, ...params }),
    enabled: !!receiverId,
  });
}


// Bên nhượng quyền nhận được đề nghị sang nhượng
export function useSellerTransferOffers(receiverId: string, params?: any) {
  return useQuery({
    queryKey: ['seller-transfer-offers', receiverId, params],
    queryFn: () => offerApi.getList({ entityType: 'BusinessTransfer', receiverId, ...params }),
    enabled: !!receiverId,
  });
}


// Người lao động nhận được đề nghị công việc
export function useMyAppliedJobs(userId: string, page = 1) {
  return useQuery({
    queryKey: ['my-applied-jobs', userId, page],
    queryFn: () => offerApi.getList({
      page,
      limit: 10,
      entityType: 'JobOffer',
      senderId: userId,
      orderBy: 'createdAt',
      orderDirection: 'DESC'
    }),
    enabled: !!userId,
  });
}

// Người mua nhận được đề nghị sang nhượng
export function useMyTransferOffers(senderId?: string) {
  return useQuery({
    queryKey: ['my-transfer-offers', senderId],
    queryFn: () => offerApi.getList({ entityType: 'BusinessTransfer', senderId }),
    enabled: !!senderId,
  });
}


// Người thuê nhận được đề nghị sang nhượng
export function useMyTransferRequestOffers(senderId?: string) {
  return useQuery({
    queryKey: ['my-transfer-request-offers', senderId],
    queryFn: () => offerApi.getList({ entityType: 'BusinessTransferRequest', senderId }),
    enabled: !!senderId,
  });
}


// Bên nhận nhượng quyền nhận được đề nghị nhượng quyền
export function useMyFranchiseOffers(senderId?: string) {
  return useQuery({
    queryKey: ['my-franchise-offers', senderId],
    queryFn: () => offerApi.getList({ entityType: 'Franchise', senderId }),
    enabled: !!senderId,
  });
}



// Bên nhận nhượng quyền nhận được đề nghị nhượng quyền (nhu cầu)
export function useMyFranchiseRequestOffers(senderId?: string) {
  return useQuery({
    queryKey: ['my-franchise-request-offers', senderId],
    queryFn: () => offerApi.getList({ entityType: 'FranchiseRequest', senderId }),
    enabled: !!senderId,
  });
}


// Phản hồi đề nghị công việc
export function useRespondOffer() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'accepted' | 'rejected' }) =>
      offerApi.updateStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recruiter-offers'] });
      addToast('success', 'Đã phản hồi hồ sơ ứng tuyển!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Phản hồi thất bại, vui lòng thử lại!')),
  });
}


// Cập nhật trạng thái ứng viên
export function useUpdateOfferStage() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: OfferStage }) =>
      offerApi.updateStage(id, stage),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recruiter-offers'] });
      addToast('success', 'Đã cập nhật trạng thái ứng viên!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật thất bại, vui lòng thử lại!')),
  });
}


// Người thuê nhận được đề nghị thuê mặt bằng
export function useMyRentalOffers(senderId?: string) {
  return useQuery({
    queryKey: ['my-rental-offers', senderId],
    queryFn: () => offerApi.getList({ entityType: 'Rental', senderId }),
    enabled: !!senderId,
  });
}

export function useMyRentalRequestOffers(senderId?: string) {
  return useQuery({
    queryKey: ['my-rental-request-offers', senderId],
    queryFn: () => offerApi.getList({ entityType: 'RentalRequest', senderId }),
    enabled: !!senderId,
  });
}


// Người thuê gửi yêu cầu thuê mặt bằng
export function useOfferRentalRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: any) => offerApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-rental-request-offers'] });
      addToast('success', 'Đã gửi đề nghị thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi đề nghị thất bại, thử lại!')),
  });
}


// Người mua nhận được đề nghị nhượng quyền (nhu cầu)
export function useOfferFranchiseRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: any) => offerApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-franchise-request-offers'] });
      addToast('success', 'Đã gửi đề nghị thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Gửi đề nghị thất bại, thử lại!')),
  });
}

